import { useMutation } from '@tanstack/react-query';
import { LoadedUser } from '../../interfaces';
import { invalidateWaivers, updateWaiver } from '../../services/waivers';
import {
  GetWaiversData,
  UpdateWaiverPayload,
  UpdateWaiverResponse,
  updateWaiverSchema,
} from '../../types/waivers';
import useUser from '../global/useUser';

interface UpdateWaiverOptions {
  autoRefetch?: boolean;
  draft?: GetWaiversData | null | undefined;
  disabled?: boolean;
  onSuccess?: (waiver: UpdateWaiverResponse | null) => void | Promise<void>;
  onError?: (error: Error) => void | Promise<void>;
}

function useUpdateWaiver({
  draft,
  disabled,
  onSuccess,
  onError,
  autoRefetch = true,
}: UpdateWaiverOptions = {}) {
  const { user } = useUser<LoadedUser>();

  const businessId = user?.business?.id;

  const mutation = useMutation({
    mutationFn: async (
      payload: Omit<UpdateWaiverPayload, 'businessId'> & {
        waiverId?: string;
      },
    ) => {
      const waiverId = payload.waiverId || draft?.id;
      if (!waiverId || disabled || !businessId) return null;

      const initialPayload = { businessId, ...payload };
      const body = await updateWaiverSchema
        .noUnknown()
        .validate(initialPayload, {
          stripUnknown: true,
        });

      const response = await updateWaiver(waiverId, body);

      if (response && autoRefetch) await invalidateWaivers();

      return response;
    },
    onSuccess,
    onError,
  });

  return {
    mutation,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
  };
}

export default useUpdateWaiver;
